<script setup lang="ts">
import 'vue3-carousel/dist/carousel.css';
import { computed, ref } from 'vue';
import { z } from 'zod';
import type { CarouselConfig } from 'vue3-carousel';
import { Carousel, Navigation, Slide } from 'vue3-carousel';
import { useFetch } from '#app/composables/fetch';
import type { MenuHistoryResponseItem } from '~~/server/api/kiwi_user/menus/history';
import { SHOW_HISTORY_MENU_KEY } from '~/constants/localStorageKeys';
import { useScreenSize } from '~/composables/useScreenSize';

const { screenSize } = useScreenSize();

const carouselConfig = computed<Partial<CarouselConfig>>(() => ({
  gap: 8,
  itemsToShow: 1,
  snapAlign: 'start',
  breakpoints: {
    769: {
      itemsToShow: 3,
    },
  },
}));

const historyIdsSchema = z.array(z.number()).min(1);
const historyIds = ref<number[]>([]);

// NOTE: クライアント側でキャッシュを持つためのキーを生成
const cacheKey = computed(
  () => `menu-history-${historyIds.value.toReversed().join(',')}`,
);

// NOTE: immediate: falseで初期実行を防ぐ。executeを使って明示的にリクエストを行う
const { data: menus, execute } = await useFetch<MenuHistoryResponseItem[]>(
  '/api/kiwi_user/menus/history',
  {
    method: 'GET',
    query: {
      id: computed(() => historyIds.value.toReversed()),
    },
    immediate: false,
    watch: false,
    key: cacheKey,
    // NOTE: クエリをキーとしてクライアント側でキャッシュを持つ
    getCachedData(key, nuxtApp) {
      return nuxtApp.payload.data[key] ?? nuxtApp.static.data[key];
    },
  },
);

// NOTE: クライアントサイドでのみ実行
if (import.meta.client) {
  try {
    const storedValue = localStorage.getItem(SHOW_HISTORY_MENU_KEY);

    if (storedValue) {
      const parsed = JSON.parse(storedValue);
      const result = historyIdsSchema.safeParse(parsed);

      if (result.success) {
        historyIds.value = result.data;
        await execute();
      }
    }
  } catch {
    // NOTE: パースに失敗した場合や想定外の値の場合は何も起きない
  }
}

const isShowNavigation = computed(() => {
  if (screenSize.value === 'pc') {
    return menus.value && menus.value.length > 3;
  }

  return menus.value && menus.value.length > 2;
});
</script>

<template>
  <div v-if="menus && menus.length > 0">
    <h2 class="mb-3 max-md:px-2 font-bold text-xl text-brown-300">
      最近見た献立
    </h2>

    <ClientOnly>
      <Carousel v-bind="carouselConfig">
        <Slide v-for="menu in menus" :key="menu.id">
          <AtomsMenuCard
            :old-identifier="menu.old_identifier"
            :menu-id="menu.id"
            :title="menu.title"
            :main-image="menu.main_image"
            :energy="menu.energy"
            :recipe-count="menu.recipes.length"
            :tags="menu.tags"
            :recipes="menu.recipes"
          />
        </Slide>

        <template #addons>
          <Navigation v-if="isShowNavigation" />
        </template>
      </Carousel>
    </ClientOnly>
  </div>
</template>

<style scoped>
@reference '~/assets/css/main.css';
:deep(.carousel) {
  --vc-nav-background: rgba(0, 0, 0, 0.3);
  --vc-nav-color: #fff;
  --vc-nav-color-hover: #fff;
  --vc-nav-border-radius: 4px;
  --vc-nav-width: 32px;
  --vc-nav-height: 48px;
  --vc-nav-box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

@media (min-width: 769px) {
  :deep(.carousel__prev),
  :deep(.carousel__next) {
    @apply transition-all duration-200 ease-in-out opacity-50;
  }
}

:deep(.carousel__prev) {
  @apply rounded-l-none;
}

:deep(.carousel__next) {
  @apply rounded-r-none;
}

:deep(.carousel:hover .carousel__prev),
:deep(.carousel:hover .carousel__next) {
  @apply opacity-100;
}

:deep(.carousel__prev--disabled),
:deep(.carousel__next--disabled) {
  @apply hidden;
}

:deep(.carousel__prev:hover),
:deep(.carousel__next:hover) {
  --vc-nav-background: rgba(0, 0, 0, 0.6);
}
</style>
